import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "./components/LandingPage";
import Dashboard from "./components/Dashboard";
import PreviousStatements from "./components/PreviousStatements";
import MyRules from "./components/MyRules";
import MergedStatements from "./components/MergedStatements";
import AuthPage from "./components/AuthPage";
import SubscriptionPage from "./components/SubscriptionPage";
import AdminPanel from "./components/AdminPanel";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Security from "./components/Security";
import AboutUs from "./components/AboutUs";
import HelpCenter from "./components/HelpCenter";
import ContactUs from "./components/ContactUs";
import TaxGuide from "./components/TaxGuide";
import ApiDocs from "./components/ApiDocs";
import PayPerUse from "./components/PayPerUse";
import CreditPackages from "./components/CreditPackages";
import BulkDiscounts from "./components/BulkDiscounts";
import FreeCredits from "./components/FreeCredits";
import TaxProDirectory from "./components/TaxProDirectory";
import TaxProPortal from "./components/TaxProPortal";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set axios default authorization header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      
      toast.success("Welcome back!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { username, email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      
      toast.success("Account created successfully!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    toast.info("Logged out successfully");
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to refresh user:', error);
        throw error; // Re-throw so calling function can handle it
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser }}>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={
              user ? <Navigate to="/dashboard" /> : <AuthPage />
            } />
            <Route path="/subscription" element={
              user ? <SubscriptionPage /> : <Navigate to="/auth" />
            } />
            <Route path="/subscription/success" element={
              user ? <SubscriptionSuccessPage /> : <Navigate to="/auth" />
            } />
            <Route path="/subscription/cancel" element={
              user ? <SubscriptionCancelPage /> : <Navigate to="/auth" />
            } />
            <Route path="/dashboard" element={
              user ? <Dashboard /> : <Navigate to="/auth" />
            } />
            <Route path="/previous-statements" element={
              user ? <PreviousStatements /> : <Navigate to="/auth" />
            } />
            <Route path="/my-rules" element={
              user ? <MyRules /> : <Navigate to="/auth" />
            } />
            <Route path="/merged-statements" element={
              user ? <MergedStatements /> : <Navigate to="/auth" />
            } />
            <Route path="/merged-statements/:mergedId" element={
              user ? <MergedStatements /> : <Navigate to="/auth" />
            } />
            <Route path="/grand-statements" element={
              user ? <MergedStatements kind="grand" /> : <Navigate to="/auth" />
            } />
            <Route path="/grand-statements/:mergedId" element={
              user ? <MergedStatements kind="grand" /> : <Navigate to="/auth" />
            } />
            <Route path="/admin" element={
              user && user.is_admin ? <AdminPanel /> : <Navigate to="/dashboard" />
            } />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/security" element={<Security />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/tax-guide" element={<TaxGuide />} />
            <Route path="/api-docs" element={<ApiDocs />} />
            <Route path="/pay-per-use" element={<PayPerUse />} />
            <Route path="/credit-packages" element={<CreditPackages />} />
            <Route path="/bulk-discounts" element={<BulkDiscounts />} />
            <Route path="/free-credits" element={<FreeCredits />} />
            <Route path="/find-tax-pro" element={<TaxProDirectory />} />
            <Route path="/tax-pro-portal" element={<TaxProPortal />} />
          </Routes>
          <Toaster />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

// Success Page Component
const SubscriptionSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setSessionId(sessionId);
      checkPaymentStatus(sessionId);
    }
  }, []);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/credits/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success("Subscription activated successfully!");
        
        // Refresh user data
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 3000);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue checking
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'checking' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Processing Payment</h2>
              <p className="text-slate-600">Please wait while we confirm your subscription...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Welcome to Tax Helper Pros!</h2>
              <p className="text-slate-600 mb-4">Your subscription has been activated successfully.</p>
              <p className="text-sm text-slate-500">Redirecting to dashboard in 3 seconds...</p>
            </>
          )}
          
          {(status === 'failed' || status === 'error' || status === 'timeout') && (
            <>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Payment Issue</h2>
              <p className="text-slate-600 mb-4">There was an issue processing your payment. Please try again.</p>
              <button
                onClick={() => window.location.href = '/subscription'}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Cancel Page Component
const SubscriptionCancelPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4">
        <div className="text-center">
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Payment Cancelled</h2>
          <p className="text-slate-600 mb-6">Your payment was cancelled. No charges were made to your account.</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/subscription'}
              className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="w-full bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;